import { AgGridReact } from "ag-grid-react"
import { useCallback, useMemo, useRef } from "react"

import {
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  ModelUpdatedEvent,
  RowClassParams,
  RowDataUpdatedEvent,
} from "ag-grid-community"
import { Column, ContextMenuItem, Mask, PinnedBottomRowStyle, ScrollPosition } from "../../types"
import {
  RowStyleProtocol,
  columnsDefValueFormatterMapping,
  getCellStylePipeline,
  rowStyleMapping,
} from "../agGridMapping"

export interface IContextMenuItemDef {
  contextMenuItem: ContextMenuItem
  shouldEnableMenuItem?: (params: any) => boolean
  action?: (params: any) => void
}

export type ColumnSizingStrategy = "sizeToFit" | "autoSize" | "none"

interface IUseAgGridProps {
  columns: Column[]
  rowStyles?: RowStyleProtocol[]
  pinnedRowStyles?: PinnedBottomRowStyle[]
  contextMenuItemsDef?: IContextMenuItemDef[]
  initialScrollPosition?: ScrollPosition
  initialScrollColumn?: string
  columnSizingStrategy?: ColumnSizingStrategy
  masks?: Record<string, Mask[]>
}

interface IUseAgGridReturn {
  sizeColumnsToFit: () => void
  gridRef: React.RefObject<AgGridReact>
  gridOptions: GridOptions
  scrollToColumn: (columnField: string) => void
}

/**
 * Builds and returns the context menu items for AG Grid
 */
const buildContextMenuItems = (
  params: GetContextMenuItemsParams,
  contextMenuItemsDef: IContextMenuItemDef[],
  columns: Column[],
  gridRef?: React.RefObject<AgGridReact>,
): (string | MenuItemDef)[] => {
  // Our custom menu items array
  const customContextMenuItems: (string | MenuItemDef)[] = []

  // Get row data from the clicked row, if available
  const rowData = params.node?.data

  contextMenuItemsDef.forEach(item => {
    const { contextMenuItem, shouldEnableMenuItem: isEnabled, action } = item
    if (isEnabled && !isEnabled(rowData)) {
      return
    }

    if (contextMenuItem === ContextMenuItem.SEPARATOR) {
      customContextMenuItems.push("separator")
    } else if (contextMenuItem === ContextMenuItem.EXPORT) {
      // Filter columns that should be included in export
      const exportColumnKeys = columns
        .filter(column => column.export_included !== false)
        .map(column => column.field)

      // Add CSV export option
      customContextMenuItems.push({
        name: "Export to CSV",
        action: () => {
          if (gridRef?.current?.api) {
            const exportParams = {
              columnKeys: exportColumnKeys,
            }
            gridRef.current.api.exportDataAsCsv(exportParams)
          }
        },
      })

      // Add Excel export option
      customContextMenuItems.push({
        name: "Export to Excel",
        action: () => {
          if (gridRef?.current?.api) {
            const exportParams = {
              columnKeys: exportColumnKeys,
            }
            gridRef.current.api.exportDataAsExcel(exportParams)
          }
        },
      })
    } else {
      customContextMenuItems.push({
        name: contextMenuItem
          .toString()
          .split("_")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" "),
        action: action && rowData ? () => action(rowData) : undefined,
      })
    }
  })

  return customContextMenuItems
}

/**
 * Custom hook to handle ag grid resizing and columns data from Scient backend
 */
export const useAgGrid = ({
  columns,
  pinnedRowStyles,
  rowStyles,
  masks,
  contextMenuItemsDef = [{ contextMenuItem: ContextMenuItem.EXPORT }],
  initialScrollPosition,
  initialScrollColumn,
  columnSizingStrategy = "none",
}: IUseAgGridProps): IUseAgGridReturn => {
  const gridRef = useRef<AgGridReact | null>(null)

  const sizeColumnsToFit = useCallback(() => {
    gridRef.current?.api.sizeColumnsToFit()
  }, [])

  /**
   * Memoized columns data
   * "Account", "Book" and "BBSymbol" are not included because they are grouped into Path in order to build the treeData.
   */
  const columnsData = useMemo(() => {
    return columns.map(column => {
      return {
        headerName: column.headerName,
        field: column.field,
        filter: column.filter,
        valueFormatter: columnsDefValueFormatterMapping[column.valueFormatter],
        cellStyle: getCellStylePipeline(column.cellStyles, masks),
        hide: column.hide,
        headerTooltip: column.headerTooltip,
        tooltipField: column.tooltipField,
        menuTabs: column.filter ? ["filterMenuTab"] : [],
        pinned: column.pinned,
      }
    })
  }, [columns, masks])

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
    }
  }, [])

  const getRowStyle = useCallback(
    (params: RowClassParams) => {
      // For pinned rows
      if (params.node.rowPinned === "bottom") {
        const styleObject = pinnedRowStyles?.find(style => style.rowIndex === params.node.rowIndex)

        if (styleObject) {
          const rowStyleKey = styleObject.rowStyle as RowStyleProtocol
          return rowStyleMapping[rowStyleKey](params)
        }

        return undefined
      }
      // For regular rows
      if (rowStyles) {
        for (const rowStyle of rowStyles) {
          const rowStyleObject = rowStyleMapping[rowStyle](params)
          if (rowStyleObject) {
            return rowStyleObject
          }
        }
      }

      return undefined
    },
    [pinnedRowStyles, rowStyles],
  )

  /**
   * Generate context menu items for the ag-grid
   */
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      return buildContextMenuItems(params, contextMenuItemsDef, columns, gridRef)
    },
    [contextMenuItemsDef, columns],
  )

  const scrollToEnd = useCallback(() => {
    gridRef.current?.api.ensureColumnVisible(columnsData[columnsData.length - 1].field)
  }, [columnsData])

  const scrollToColumn = useCallback((columnField: string) => {
    gridRef.current?.api.ensureColumnVisible(columnField)
  }, [])

  const onRowDataUpdated = useCallback(
    (params: RowDataUpdatedEvent) => {
      if (initialScrollColumn) {
        scrollToColumn(initialScrollColumn)
      } else if (initialScrollPosition === ScrollPosition.END) {
        scrollToEnd()
      }
    },
    [initialScrollPosition, initialScrollColumn, scrollToEnd, scrollToColumn],
  )

  const onModelUpdated = useCallback(
    (params: ModelUpdatedEvent) => {
      if (initialScrollColumn) {
        scrollToColumn(initialScrollColumn)
      } else if (initialScrollPosition === ScrollPosition.END) {
        scrollToEnd()
      }
    },
    [initialScrollPosition, initialScrollColumn, scrollToEnd, scrollToColumn],
  )

  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent) => {
      // Handle autoSize strategy when data is rendered (needs data for content width calculation)
      if (columnSizingStrategy === "autoSize") {
        // Get all column IDs and auto-size them (AG Grid v27 syntax)
        const allColumnIds = params.columnApi.getAllColumns()?.map(col => col.getId()) ?? []
        // Add a small delay before auto-sizing to ensure data is properly rendered
        setTimeout(() => {
          params.columnApi.autoSizeColumns(allColumnIds)
        }, 150)
        return
      }

      if (initialScrollColumn) {
        scrollToColumn(initialScrollColumn)
      } else if (initialScrollPosition === ScrollPosition.END) {
        scrollToEnd()
      }
    },
    [columnSizingStrategy, initialScrollPosition, initialScrollColumn, scrollToEnd, scrollToColumn],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Handle sizeToFit strategy when grid is ready (doesn't need data)
      if (columnSizingStrategy === "sizeToFit") {
        params.api.sizeColumnsToFit()
      }
    },
    [columnSizingStrategy],
  )

  return {
    sizeColumnsToFit,
    gridRef,
    gridOptions: {
      columnDefs: columnsData,
      defaultColDef,
      getRowStyle,
      getContextMenuItems,
      onRowDataUpdated,
      onModelUpdated,
      onGridReady,
      onFirstDataRendered,
      enableCellTextSelection: true,
    },
    scrollToColumn,
  }
}
